// MemoryDetailPage.jsx — /spaces/:spaceId/memories/:memId
// v2.11 — NEW-107 (Sept 26, 2026): section-move cache fix. invalidateMemoriesCache now runs after
//   ANY successful edit PATCH (moved out of the photoReplaced branch), so moving a memory between
//   Private and Shared refreshes the feed cache and the tab placement is correct on return. No
//   backend/API change.
// v2.10 — W2 member tags (Sept 20, 2026): the owner can tag a contributor's shared memory (their
//   private overlay) from this read-only view; content stays read-only (NEW-66 preserved) — only tags
//   are editable. Title moved above the image in the shared view. Gated on member_tags_enabled;
//   PATCH /memories/:id { addTag | removeTagId } (server authorizes by space ownership, v2.35).
// v2.9 — NEW-66: read-only for a contributor's memory. A member/owner can VIEW a shared
//   contributor memory but not edit it — the owner acts only via the review/consent workflow
//   (server also 404s direct mutation). Renders content only (no Save/Cancel/Remove/Change-photo/
//   privacy toggle); the voice->RecordPage auto-redirect is skipped for contributor memories so
//   they don't bounce into the editor. Owner memories unchanged. (2026-09-04)
// v2.8 — 4.3 relabel: "Remove/Keep" register, "trash" retired (July 24, 2026)
//   Copy v0.12 §1a (D-C7). Copy-only: affordance + DeleteConfirmModal strings
//   → Remove; single-memory body → Recently Deleted. No logic change.
// v2.7 — Delete memory: soft-delete with confirmation modal (May 17, 2026)
//
// Changes from v2.6:
//   - NEW: "Delete memory" button below Cancel in the actions area.
//     Red text link style (secondary to save/cancel). Opens DeleteConfirmModal.
//   - NEW state: showDeleteConfirm, deleting.
//   - NEW handler: handleDelete (opens modal), handleConfirmDelete (calls
//     DELETE /memories/:id, invalidates cache, navigates to feed on success).
//   - Import: DeleteConfirmModal.
//   - Zero changes to save, photo change, leave-without-saving, or any
//     existing handlers.
//
// v2.6 — Change photo: file picker + S3 upload + PATCH (May 17, 2026)
//
// Changes from v2.5:
//   - NEW: Photo replacement for photo memories. "Change photo" button
//     below the photo opens the file picker immediately (no confirmation
//     modal). Selecting a new file shows a local blob preview. Save
//     uploads the new file to S3 (POST /media/upload-url → PUT) then
//     PATCHes the memory with newPhotoS3Key. Follows the voice re-record
//     pattern from RecordPage v5.x (ADR — Voice Recording Session Log v2).
//   - NEW state: newPhotoFile, newPhotoPreview (derived via canonical
//     URL.createObjectURL useEffect with revokeObjectURL cleanup).
//   - NEW ref: photoFileInputRef (hidden file input).
//   - NEW handlers: handleChangePhoto (opens picker), handlePhotoFileSelect
//     (validates + sets file), validatePhotoFile (JPEG/PNG/WebP, ≤10MB).
//   - MODIFIED handleSaveEdit: if newPhotoFile exists, uploads to S3 first,
//     then includes newPhotoS3Key in the PATCH body. On success, updates
//     local memory.s3Key and photoUrl state.
//   - MODIFIED hasUnsavedChanges: includes newPhotoFile !== null.
//   - MODIFIED photo section JSX: newPhotoPreview takes precedence over
//     signed photoUrl. "Change photo" button added. Hidden file input added.
//   - No intermediate view mode introduced. Edit screen is the only screen.
//   - Imports: added invalidateMemoriesCache from MemoryFeed.
//
// Previous changes (v2.5):
//   - Defensive .replace('{name}', space.name) on memory.promptText before
//     passing to PromptBanner. Handles any stored prompt_text rows that
//     contain the raw {name} placeholder (migration 019 backfill edge case).
//   - SC-x banner polish: "Updated" → "Your changes are saved"
//
// Previous changes (v2.4):
//   - I-5 fix: Removed the entire view-mode render block. Component always
//     renders in edit mode. If mounted without editing:true in location.state,
//     auto-sets editing = true. No intermediate view page ever appears.
//     Back arrow uses navigate(-1) (browser history pop) instead of
//     setEditing(false).
//
//   - I-4 fix: Replaced plain "Edit Memory" header with shared <LovedOneBar>
//     component showing space avatar + name + contextual subtitle
//     ("Edit text memory" / "Edit photo memory").
//
//   - I-7 fix: Added <PromptBanner> after LovedOneBar showing the memory's
//     denormalized promptText (populated by migration 019 + Lambda v2.5).
//     Memories without promptText simply don't show the banner.
//
//   - I-6: Added LeaveConfirmModal. Back arrow checks for unsaved changes
//     (comparing current form state to original memory values). If dirty,
//     shows modal; if clean, navigates back directly.
//
//   - Removed: handleBack (view-mode), handleFavorite, setEditing(true/false)
//     calls, formatDate helper, spaceInitial derived value — all view-mode
//     only code.
//
//   - Kept unchanged: handleSaveEdit, handleCancelEdit, PATCH payload,
//     field diffing, memory state update on success, savedToast/banner,
//     setTimeout cleanup, voice redirect, photo signed URL fetch, loading,
//     error states. All byte-identical to v2.3.
//
// Previous changes (v2.3 — Under-header "Updated" banner, April 17, 2026)
// v2.2 — Save shows "Updated" toast then navigates to feed (April 17, 2026)
// v2.1 — Edit mode displays photo above caption field (April 16, 2026)
// v2.0 — Voice edit removed; voice memories route to RecordPage (April 3, 2026)
//
// APIs: PATCH /memories/:id, GET /media/playback/:key

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { createApiClient } from '../api/client';
import { useResolvedPhotoUrl } from '../hooks/useResolvedPhotoUrl';
import LovedOneBar from '../components/LovedOneBar';
import PromptBanner from '../components/PromptBanner';
import MemoryTags from '../components/MemoryTags';
import LeaveConfirmModal from '../components/LeaveConfirmModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import DictateButton from '../components/DictateButton';
import { invalidateMemoriesCache } from '../components/MemoryFeed';
import styles from './MemoryDetailPage.module.css';
import { useAppContext } from '../App';

// v2.3: Toast duration before auto-navigate to feed after successful save.
// Bumped from 1500 → 3000 (Red Hat / Material Design 3 guidance).
const SAVED_TOAST_MS = 3000;

// v2.6: Photo replacement validation (same constants as PhotoPage).
const ACCEPTED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_PHOTO_SIZE = 10 * 1024 * 1024; // 10 MB

export default function MemoryDetailPage() {
  const { spaceId, memId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();
  // W3 inc2: text edit re-uploads the words to the memory's existing key (Option A); else inline note.
  const appState = useAppContext();
  const textAsObjectEnabled = appState?.flags?.text_as_object_enabled === true;
  const memberTagsEnabled = appState?.flags?.member_tags_enabled === true;

  // Memory data
  const [memory, setMemory] = useState(location.state?.memory || null);
  const [space, setSpace] = useState(null);
  // v2.4: Resolve S3 key to signed CloudFront URL for LovedOneBar avatar.
  const resolvedPhotoUrl = useResolvedPhotoUrl(space, getAccessTokenSilently);
  // v2.4: editing state is always true — view mode removed.
  // Kept as state (not a constant) because the init-edit-fields effect
  // depends on it, and the existing pattern works cleanly.
  const [editing, setEditing] = useState(true);
  const [editTitle, setEditTitle] = useState('');
  const [editNote, setEditNote] = useState('');
  const [editPrivate, setEditPrivate] = useState(true);
  const [saving, setSaving] = useState(false);
  // v2.2: Post-save confirmation state. Drives the under-header banner in v2.3.
  const [savedToast, setSavedToast] = useState(false);
  // Photo
  const [photoUrl, setPhotoUrl] = useState(null);
  // UI
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(!location.state?.memory);

  // v2.6: Leave-without-saving modal state (I-6).
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // v2.7: Delete confirmation modal state.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // v2.6: Photo replacement state.
  // newPhotoFile holds the File object from the picker (null until user selects).
  // newPhotoPreview is derived via useEffect (canonical blob URL pattern).
  const [newPhotoFile, setNewPhotoFile] = useState(null);
  const [newPhotoPreview, setNewPhotoPreview] = useState(null);
  const [tagNotice, setTagNotice] = useState(null); // null | true | 'limit'

  // W2 (v2.10): the owner tags a contributor's shared memory (their private overlay). Content stays
  // read-only (NEW-66) — only tags are editable. PATCH /memories/:id { addTag | removeTagId }; the
  // server authorizes by space ownership (anamoria-memories v2.35). Add = server-reconciled; remove
  // = optimistic + rollback. Mirrors MomentDetailPage's per-piece handlers.
  const handleAddTag = useCallback(async (label) => {
    const clean = (label || '').trim();
    if (!clean || !memory) return;
    setTagNotice(null);
    try {
      const api = createApiClient(getAccessTokenSilently);
      const result = await api.patch(`/memories/${memory.id}`, { addTag: clean });
      if (result && Array.isArray(result.tags)) setMemory((prev) => (prev ? { ...prev, tags: result.tags } : prev));
      invalidateMemoriesCache(spaceId);
    } catch (err) {
      console.error('Add tag failed:', err);
      setTagNotice(err && err.status === 409 ? 'limit' : true);
    }
  }, [getAccessTokenSilently, spaceId, memory]);

  const handleRemoveTag = useCallback(async (tagId) => {
    if (!memory) return;
    setTagNotice(null);
    const prevTags = memory.tags || [];
    setMemory((prev) => (prev ? { ...prev, tags: (prev.tags || []).filter((t) => t.id !== tagId) } : prev));
    try {
      const api = createApiClient(getAccessTokenSilently);
      const result = await api.patch(`/memories/${memory.id}`, { removeTagId: tagId });
      if (result && Array.isArray(result.tags)) setMemory((prev) => (prev ? { ...prev, tags: result.tags } : prev));
      invalidateMemoriesCache(spaceId);
    } catch (err) {
      console.error('Remove tag failed:', err);
      setMemory((prev) => (prev ? { ...prev, tags: prevTags } : prev));
      setTagNotice(true);
    }
  }, [getAccessTokenSilently, spaceId, memory]);

  // v2.2: Hold the pending-nav timer so we can clear it on unmount.
  const savedNavTimerRef = useRef(null);
  // v2.6: Hidden file input for photo replacement.
  const photoFileInputRef = useRef(null);

  // ─── Redirect voice memories to RecordPage ───
  useEffect(() => {
    if (memory && (memory.category || '').toLowerCase() === 'voice' && memory.creatorType !== 'contributor') {
      navigate(`/spaces/${spaceId}/record`, {
        state: { editMode: true, editMemory: memory },
        replace: true,
      });
    }
  }, [memory, spaceId, navigate]);

  // ─── Load memory + space data ───
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const api = createApiClient(getAccessTokenSilently);
      try {
        const spaceData = await api.get(`/spaces/${spaceId}`);
        if (cancelled) return;
        setSpace(spaceData);

        if (!memory) {
          const feedData = await api.get(`/spaces/${spaceId}/memories?limit=100&offset=0`);
          const found = (feedData.memories || []).find(m => m.id === memId);
          if (found) {
            setMemory(found);
          } else {
            setError('Memory not found.');
          }
        }
      } catch (err) {
        console.error('Detail load error:', err);
        if (!cancelled) setError('Failed to load memory.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [spaceId, memId, getAccessTokenSilently]);

  // ─── Fetch photo signed URL ───
  useEffect(() => {
    if (!memory) return;
    const category = (memory.category || '').toLowerCase();
    if (category !== 'photo' || !memory.s3Key) return;

    let cancelled = false;
    async function loadPhoto() {
      try {
        const api = createApiClient(getAccessTokenSilently);
        const data = await api.get(`/media/playback/${encodeURIComponent(memory.s3Key)}`);
        if (!cancelled) setPhotoUrl(data.playbackUrl);
      } catch (err) {
        console.error('Photo load error:', err);
      }
    }
    loadPhoto();
    return () => { cancelled = true; };
  }, [memory, getAccessTokenSilently]);

  // ─── Init edit fields ───
  useEffect(() => {
    if (editing && memory) {
      setEditTitle(memory.title || '');
      setEditNote(memory.note || '');
      setEditPrivate(memory.isPrivate ?? true);
    }
  }, [editing, memory]);

  // v2.2: Clear any pending nav timer on unmount so we don't navigate after
  // the component is gone (React 18 strict-mode safe).
  useEffect(() => {
    return () => {
      if (savedNavTimerRef.current) {
        clearTimeout(savedNavTimerRef.current);
        savedNavTimerRef.current = null;
      }
    };
  }, []);

  // v2.6: Derive newPhotoPreview from newPhotoFile (canonical pattern,
  // MDN + React community — identical to PhotoPage lines 182-193).
  useEffect(() => {
    if (!newPhotoFile) {
      setNewPhotoPreview(null);
      return;
    }
    const url = URL.createObjectURL(newPhotoFile);
    setNewPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [newPhotoFile]);

  // ─── Handlers ───

  // v2.4: Dictation handler for text memory edit — appends transcript to
  // editNote, matching WritePage's handleDictation pattern.
  const handleDictation = useCallback((transcript) => {
    setEditNote((prev) => {
      const separator = prev && !prev.endsWith(' ') ? ' ' : '';
      const next = prev + separator + transcript;
      return next.length <= 5000 ? next : next.substring(0, 5000);
    });
  }, []);

  // v2.6: Photo replacement handlers.
  // Opens file picker immediately — no confirmation modal (per decision).
  const handleChangePhoto = useCallback(() => {
    if (photoFileInputRef.current) {
      photoFileInputRef.current.value = '';
      photoFileInputRef.current.click();
    }
  }, []);

  // v2.6: Validate selected file and set state.
  const handlePhotoFileSelect = useCallback((e) => {
    const selected = e.target.files?.[0];
    if (!selected) return; // user cancelled picker
    if (!ACCEPTED_PHOTO_TYPES.includes(selected.type)) {
      setError('Please select a JPEG, PNG, or WebP image.');
      return;
    }
    if (selected.size > MAX_PHOTO_SIZE) {
      setError('Photo must be under 10 MB.');
      return;
    }
    setError(null);
    setNewPhotoFile(selected);
  }, []);

  // v2.4: Dirty detection for leave-without-saving modal (I-6).
  // Compares current form state to original memory values.
  // v2.6: Added newPhotoFile — selecting a replacement photo is dirty.
  const hasUnsavedChanges = useCallback(() => {
    if (!memory) return false;
    return (
      editTitle !== (memory.title || '') ||
      editNote !== (memory.note || '') ||
      editPrivate !== (memory.isPrivate ?? true) ||
      newPhotoFile !== null
    );
  }, [editTitle, editNote, editPrivate, memory, newPhotoFile]);

  // v2.4: Edit-mode back arrow handler (I-5 + I-6).
  // If dirty, show leave-confirm modal. If clean, navigate(-1).
  const handleEditBack = useCallback(() => {
    if (hasUnsavedChanges()) {
      setShowLeaveConfirm(true);
    } else {
      navigate(-1);
    }
  }, [hasUnsavedChanges, navigate]);

  // v2.2: Cancel from feed-originated edit → go back to feed. No view mode.
  const handleCancelEdit = useCallback(() => {
    navigate(`/spaces/${spaceId}`);
  }, [navigate, spaceId]);

  // v2.7: Delete memory — opens confirmation modal.
  const handleDelete = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  // v2.7: Confirmed delete — calls DELETE /memories/:id (soft-delete).
  const handleConfirmDelete = useCallback(async () => {
    if (deleting) return;
    setDeleting(true);
    setError(null);
    try {
      const api = createApiClient(getAccessTokenSilently);
      await api.post(`/memories/${memory.id}/delete`);
      // Invalidate feed cache so deleted memory disappears on navigation.
      invalidateMemoriesCache(spaceId);
      // Navigate to feed — no toast, immediate redirect.
      navigate(`/spaces/${spaceId}`, { replace: true });
    } catch (err) {
      console.error('Delete error:', err);
      setError("Couldn't delete this memory. Please try again.");
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  }, [deleting, memory, getAccessTokenSilently, spaceId, navigate]);

  const handleSaveEdit = useCallback(async () => {
    if (saving || savedToast) return;
    setSaving(true);
    setError(null);
    try {
      const api = createApiClient(getAccessTokenSilently);
      const updates = {};
      if (editTitle !== (memory.title || '')) updates.title = editTitle.trim() || null;
      if (editNote !== (memory.note || '')) {
        if (textAsObjectEnabled && memory.category === 'text') {
          const t = await api.post('/media/upload-url', { spaceId, mimeType: 'text/plain', mediaType: 'text', memoryId: memory.id });
          await api.putS3(t.uploadUrl, new Blob([editNote.trim()], { type: 'text/plain' }), 'text/plain');
          updates.textContentKey = t.s3Key;
        } else {
          updates.note = editNote.trim() || null;
        }
      }
      if (editPrivate !== memory.isPrivate) updates.isPrivate = editPrivate;

      // v2.6: If user selected a replacement photo, upload to S3 first,
      // then include newPhotoS3Key in the PATCH body. Same 2-step pattern
      // as PhotoPage (POST /media/upload-url → PUT to S3).
      if (newPhotoFile) {
        const mimeType = newPhotoFile.type.split(';')[0];
        const uploadData = await api.post('/media/upload-url', {
          spaceId,
          mimeType,
          mediaType: 'photo',
        });
        await api.putS3(uploadData.uploadUrl, newPhotoFile, mimeType);
        updates.newPhotoS3Key = uploadData.s3Key;
      }

      if (Object.keys(updates).length > 0) {
        const result = await api.patch(`/memories/${memory.id}`, updates);
        setMemory(prev => ({
          ...prev,
          title: result.title ?? prev.title,
          isPrivate: result.isPrivate ?? prev.isPrivate,
          note: editNote.trim() || prev.note,
          // v2.6: If photo was replaced, update s3Key so any subsequent
          // re-render or state reference has the new key.
          s3Key: result.s3Key ?? prev.s3Key,
        }));
        // v2.6: If photo was replaced, update displayed photo URL to the
        // local preview (the signed URL for the new key isn't available yet
        // but the blob preview is still alive). Also clear the file state.
        if (result.photoReplaced) {
          setPhotoUrl(newPhotoPreview);
          setNewPhotoFile(null);
        }
        // v2.11 (NEW-107): invalidate the feed cache after ANY successful edit so
        // Private/Shared placement (and title/note/photo) is fresh on return. Matches
        // the tag-edit and delete paths; previously this ran only when the photo was
        // replaced, so a privacy-only move showed stale tab placement until the cache TTL.
        invalidateMemoriesCache(spaceId);
      }

      // v2.2: Show "Updated" confirmation, then navigate to feed after
      // SAVED_TOAST_MS. No intermediate view mode.
      // v2.3: Confirmation now renders as an under-header banner (not inline).
      setSavedToast(true);
      savedNavTimerRef.current = setTimeout(() => {
        navigate(`/spaces/${spaceId}`);
      }, SAVED_TOAST_MS);
    } catch (err) {
      console.error('Save edit error:', err);
      setError("Couldn't save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [saving, savedToast, editTitle, editNote, editPrivate, memory, getAccessTokenSilently, navigate, spaceId, newPhotoFile, newPhotoPreview]);

  // ─── Derived ───
  const category = (memory?.category || '').toLowerCase();
  const isText = category === 'text';
  const isPhoto = category === 'photo';

  // ─── Loading ───
  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingDot} />
        <span>Loading...</span>
      </div>
    );
  }

  if (!memory) {
    return (
      <div className={styles.errorScreen}>
        <p>{error || 'Memory not found.'}</p>
        <button className={styles.btnPrimary} onClick={() => navigate(`/spaces/${spaceId}`)}>Go back</button>
      </div>
    );
  }

  // Voice memories redirect (safety — should not render)
  // NEW-66: a contributor's memory is read-only to members/owner (view, never edit).
  if (memory.creatorType === 'contributor') {
    return (
      <div className={styles.screen}>
        <LovedOneBar
          spaceName={space?.name || ''}
          spacePhotoUrl={resolvedPhotoUrl}
          subtitle={memory.creatorName ? `Shared by ${memory.creatorName}` : 'Shared memory'}
          onBack={() => navigate(`/spaces/${spaceId}`)}
          backLabel="Back"
        />
        <div className={styles.content}>
          {memory.title && <h2 className={styles.readonlyTitle}>{memory.title}</h2>}
          {isPhoto && photoUrl && (
            <div className={styles.photoContainer}>
              <img src={photoUrl} alt={memory.title || 'Photo'} className={styles.photoImg} />
            </div>
          )}
          {memberTagsEnabled && (
            <MemoryTags
              tags={memory.tags || []}
              onAdd={handleAddTag}
              onRemove={handleRemoveTag}
            />
          )}
          {tagNotice && (
            <p className={styles.readonlyNote} role="alert">
              {tagNotice === 'limit'
                ? 'You can add up to five tags to a memory.'
                : "We weren't able to save that tag. When you're ready, please try again."}
            </p>
          )}
          {memory.note && <p className={styles.readonlyNote}>{memory.note}</p>}
          {!isPhoto && !isText && (
            <p className={styles.readonlyNote}>This is a shared {category} memory.</p>
          )}
        </div>
      </div>
    );
  }

  if (category === 'voice') return null;

  // ═══════════════════════════════════════
  //  EDIT MODE (always — view mode removed in v2.4)
  // ═══════════════════════════════════════
  return (
    <div className={styles.screen}>
      {/* v2.4 I-4 fix: Shared LovedOneBar replaces plain "Edit Memory" header.
          Back arrow uses dirty-check guard (I-5 + I-6). */}
      <LovedOneBar
        spaceName={space?.name || ''}
        spacePhotoUrl={resolvedPhotoUrl}
        subtitle={`Edit ${isPhoto ? 'photo' : 'text'} memory`}
        onBack={handleEditBack}
        backLabel="Back"
      />

      {/* v2.4 I-7 fix: PromptBanner showing the memory's denormalized
          promptText (populated by migration 019 + Lambda v2.5).
          Memories without promptText simply don't show the banner. */}
      {memory.promptText && (
        <PromptBanner
          prompt={{ text: memory.promptText?.replace('{name}', space?.name || ''), title: memory.promptTitle || 'CONTRIBUTE' }}
          showSkip={false}
          fullWidth
        />
      )}

      {/* v2.3: Under-header "Updated" banner — page-level status chrome.
          Rendered between header and .content so it sits under the sticky
          header and above the form. Auto-dismiss is handled by the
          setTimeout in handleSaveEdit which then navigates to the feed. */}
      {savedToast && (
        <div
          className={styles.updatedBanner}
          role="status"
          aria-live="polite"
        >
          <div className={styles.updatedBannerInner}>
            <span className={styles.updatedBannerIcon} aria-hidden="true">✓</span>
            <span className={styles.updatedBannerText}>Your changes are saved</span>
          </div>
        </div>
      )}

      <div className={styles.content}>
        {/* Title */}
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Title</label>
          <input
            type="text"
            className={styles.fieldInput}
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            placeholder="Give it a title (optional)"
            maxLength={75}
            disabled={savedToast}
          />
        </div>

        {/* Note (text memories) — with dictation support */}
        {isText && (
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Memory text</label>
            <div className={styles.textareaWrap}>
              <textarea
                className={styles.fieldTextarea}
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                placeholder="Your memory..."
                maxLength={5000}
                rows={8}
                disabled={savedToast}
              />
              {!savedToast && (
                <div className={styles.dictatePosition}>
                  <DictateButton onTranscript={handleDictation} size="medium" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Caption (photo memories) */}
        {isPhoto && (
          <>
            {/* v2.6: Hidden file input for photo replacement */}
            <input
              ref={photoFileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className={styles.fileInput}
              onChange={handlePhotoFileSelect}
            />

            {/* v2.1: Show the photo above the caption field in edit mode.
                v2.6: newPhotoPreview (local blob) takes precedence over
                photoUrl (signed CloudFront URL for existing photo). */}
            {(newPhotoPreview || photoUrl) && (
              <div className={styles.photoContainer}>
                <img
                  src={newPhotoPreview || photoUrl}
                  alt={memory.title || 'Photo'}
                  className={styles.photoImg}
                />
              </div>
            )}

            {/* v2.6: Change photo button — opens file picker immediately */}
            <button
              className={styles.changePhotoBtn}
              onClick={handleChangePhoto}
              disabled={saving || savedToast}
            >
              Change photo
            </button>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Caption</label>
              <textarea
                className={styles.fieldTextarea}
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                placeholder="What's the story behind this photo?"
                maxLength={2000}
                rows={4}
                disabled={savedToast}
              />
            </div>
          </>
        )}

        {/* Privacy toggle */}
        <div
          className={styles.privacyRow}
          onClick={() => { if (!savedToast) setEditPrivate((p) => !p); }}
        >
          <div className={styles.privacyLabel}>
            <span className={styles.privacyIcon}>{editPrivate ? '🔒' : '👥'}</span>
            <span className={styles.privacyText}>
              {editPrivate ? 'Private — only you' : 'Shared with family'}
            </span>
          </div>
          <div className={`${styles.toggleTrack} ${editPrivate ? styles.togglePrivate : styles.toggleShared}`}>
            <div className={styles.toggleThumb} />
          </div>
        </div>

        {error && <div className={styles.errorBanner}>{error}</div>}

        <div className={styles.actions}>
          <button
            className={styles.btnPrimary}
            onClick={handleSaveEdit}
            disabled={saving || savedToast}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            className={styles.btnGhost}
            onClick={handleCancelEdit}
            disabled={saving || savedToast}
          >
            Cancel
          </button>
          {/* v2.7: Delete memory — red text link below Cancel */}
          <button
            className={styles.btnDelete}
            onClick={handleDelete}
            disabled={saving || savedToast || deleting}
          >
            Remove memory
          </button>
        </div>
      </div>

      {/* v2.4 I-6: Leave-without-saving modal */}
      <LeaveConfirmModal
        open={showLeaveConfirm}
        message="Your changes won't be saved if you leave now."
        icon="edit"
        onKeepEditing={() => setShowLeaveConfirm(false)}
        onLeave={() => {
          setShowLeaveConfirm(false);
          navigate(-1);
        }}
      />

      {/* v2.7: Delete confirmation modal */}
      <DeleteConfirmModal
        open={showDeleteConfirm}
        title="Remove this memory?"
        message="This memory will move to Recently Deleted. You can bring it back anytime in the next 30 days."
        confirmLabel="Remove memory"
        cancelLabel="Keep memory"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        loading={deleting}
      />
    </div>
  );
}
